// src/pages/HomeInstitutional.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SliderSection from "../components/common/SliderSection";
import CourseCard from "../components/common/CourseCard";
import { useSettings } from "../contexts/SettingsContext";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import api from "../api/api";
import Spinner from "../components/common/Spinner";
import { format, isValid } from "date-fns";
import GalleryPhotoSlider from "../components/common/GallerySlider";

// Helper para limpar HTML excessivo em previews de texto
const safeParseContent = (html) => {
  if (!html) return "";
  // Remove tags de imagem e outros elementos que podem quebrar o card
  return html.replace(/<img[^>]*>/g, "").replace(/<iframe[^>]*>/g, "");
};

const getPublicationThumb = (item, settings) => {
  if (!item) return { type: "default", src: settings?.SITE_LOGO };
  const rawUrl = item?.attachment_url || item?.media?.url || item?.media_url || item?.file_url || item?.url || null;
  const fallback = settings?.SITE_LOGO || "/img/logo-placeholder.png";
  if (!rawUrl) return { type: "default", src: fallback };
  const url = String(rawUrl).replace(/^http:\/\//i, "https://");
  const type = item?.media?.type || item?.type || "";

  if (type === "pdf" || url.toLowerCase().endsWith(".pdf")) return { type: "pdf", src: fallback };
  if (type === "youtube" || url.includes("youtube.com") || url.includes("youtu.be")) {
    const id = url.includes("v=") ? url.split("v=")[1]?.split("&")[0] : url.split("/").pop();
    return { type: "youtube", src: `https://img.youtube.com/vi/${id}/hqdefault.jpg` };
  }
  if (type === "image" || /\.(jpg|jpeg|png|gif|webp|avif)$/i.test(url)) return { type: "image", src: url };
  return { type: "default", src: fallback };
};

const formatDateSafe = (dateStr) => {
  if (!dateStr) return "Recente";
  const d = new Date(dateStr);
  return isValid(d) ? format(d, "dd/MM/yyyy") : "Recente";
};

export default function HomeInstitutional() {
  const { settings, loading: settingsLoading } = useSettings();
  const { user } = useAuth();
  const { addToCart, clearCart } = useCart();
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [products, setProducts] = useState([]);
  const [threads, setThreads] = useState([]);
  const [threadIndex, setThreadIndex] = useState(0);
  const [articles, setArticles] = useState([]);
  const [articleIndex, setArticleIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const bgColor = settings?.SITE_BACKGROUND_COLOR || "#f8fafc";

  const handlePurchase = (product) => {
    if (!user) {
      navigate("/login");
      return;
    }
    clearCart();
    addToCart({
      id: product.id,
      title: product.name,
      price: product.price,
      cover_image_url: product.image_url || settings.SITE_LOGO,
      type: 'product'
    });
    navigate("/checkout");
  };

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    root.style.setProperty('background-color', bgColor, 'important');
    body.style.setProperty('background-color', bgColor, 'important');
    return () => {
      root.style.backgroundColor = "";
      body.style.backgroundColor = "";
    };
  }, [bgColor]);

  useEffect(() => {
    async function loadData() {
      try {
        const [cRes, fRes, aRes, pRes] = await Promise.all([
          api.get("/courses/featured").catch(() => ({ data: { courses: [] } })),
          api.get("/forum/home/threads?limit=10").catch(() => ({ data: { data: [] } })),
          api.get("/admin/publications").catch(() => ({ data: { data: [] } })),
          api.get("/products").catch(() => ({ data: { products: [] } }))
        ]);

        setCourses(cRes.data?.courses || []);
        setThreads(fRes.data?.data || []);
        setArticles((aRes.data?.data || []).filter(p => p.status === "publicado"));
        setProducts(pRes.data?.products || (Array.isArray(pRes.data) ? pRes.data : []));
      } catch (e) {
        console.error("Erro crítico ao carregar dados da Home:", e);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    if (threads.length <= 1) return;
    const t = setInterval(() => setThreadIndex(i => (i + 1) % threads.length), 5000);
    return () => clearInterval(t);
  }, [threads.length]);

  useEffect(() => {
    if (articles.length <= 1) return;
    const t = setInterval(() => setArticleIndex(i => (i + 1) % articles.length), 6000);
    return () => clearInterval(t);
  }, [articles.length]);

  if (settingsLoading || loading) return <div className="min-h-screen flex items-center justify-center"><Spinner /></div>;

  return (
    <div 
      className="home-wrapper w-full animate-fadeIn transition-all duration-700"
      style={{ backgroundColor: bgColor }}
    >
      {/* HERO */}
      <section className="py-12 text-center">
        <div className="text-center px-4">
          <h1 className="text-4xl md:text-5xl font-extrabold leading-tight text-[#053b50]">
            Transforme seus estudos com {" "}
            <span className="text-[#053b50]">{settings.SITE_NAME}</span>
          </h1>
          <p className="mt-6 text-lg text-gray-600 max-w-3xl mx-auto italic">
            Cursos, materiais exclusivos e uma experiência moderna de aprendizado.
          </p>
        </div>
        <div className="mt-10 flex justify-center gap-4 flex-wrap">
          <Link to="/cadastro" className="px-8 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 shadow-lg transition-all active:scale-95">
            Começar Agora
          </Link>
          <Link to="/login" className="px-8 py-3 bg-white text-gray-700 border border-gray-200 rounded-xl font-bold hover:bg-gray-50 transition-all">
            Já tenho conta
          </Link>
        </div>
      </section>

      {settings.HOME_SLIDER_ENABLED && (
        <div className="my-4">
          <SliderSection />
        </div>
      )}

      {/* CURSOS EM DESTAQUE */}
      <section className="py-8">
        <div className="container mx-auto px-6">
          <div className="flex items-center gap-4 mb-8">
            <div className="h-1 w-12 bg-indigo-600 rounded-full"></div>
            <h2 className="text-2xl font-black uppercase tracking-tighter">Cursos em Destaque</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.length > 0 ? (
              courses.slice(0, 3).map((course) => <CourseCard key={course.id} course={course} />)
            ) : (
              <p className="text-gray-400 italic">Nenhum curso em destaque no momento.</p>
            )}
          </div>
        </div>
      </section>

      

      {/* COMUNIDADE E PUBLICAÇÕES */}
      <section className="py-12 overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="flex flex-col items-center mb-16 text-center">
            <div className="relative inline-block px-10 py-3 mb-4">
              <div className="absolute inset-0 bg-indigo-600 transform -skew-x-12 rounded-lg shadow-[0_0_20px_rgba(79,70,229,0.4)]"></div>
              <h2 className="relative text-2xl md:text-3xl font-extrabold text-white flex items-center gap-3">
                Comunidades e Publicações <span className="text-2xl animate-bounce">🗣️</span>
              </h2>
            </div>
            <p className="text-gray-500 mt-2 font-medium">Participe das discussões e fique por dentro das novidades.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 min-h-[600px] items-start">
            {/* FÓRUM */}
            <div className="flex flex-col items-center w-full">
              <div className="mb-10 w-full max-w-sm bg-indigo-600 p-4 rounded-2xl transform -skew-x-12 text-white font-black text-center shadow-lg uppercase tracking-widest">Fórum</div>
              <div className="relative flex items-center justify-center perspective-2000 h-[450px] w-full">
                {threads.length > 0 ? threads.map((t, index) => {
                  let pos = (index - threadIndex + threads.length) % threads.length;
                  let styles = pos === 0 ? { transform: "translateZ(300px)", opacity: 1, zIndex: 40 } :
                               pos === 1 ? { transform: "translateX(50%) scale(0.8)", opacity: 0.3, zIndex: 10 } :
                               pos === threads.length - 1 ? { transform: "translateX(-50%) scale(0.8)", opacity: 0.3, zIndex: 10 } :
                               { transform: "translateZ(-500px)", opacity: 0 };
                  const thumb = getPublicationThumb(t, settings);
                  return (
                    <div key={t.id} style={styles} onClick={() => navigate(`/forum?thread=${t.id}`)} className="absolute w-full max-w-md transition-all duration-1000 cursor-pointer">
                      <div className="bg-white rounded-[2.5rem] shadow-2xl overflow-hidden border border-gray-100 group">
                        <div className="relative h-48 overflow-hidden flex items-center justify-center bg-gray-50">
                          <img src={thumb.src} className={`relative z-10 transition-all duration-700 ${thumb.type === 'image' ? 'w-full h-full object-cover group-hover:scale-110' : 'w-[85%] h-[85%] object-contain'}`} alt={t.title} />
                          <div className="absolute top-4 left-4 bg-indigo-600 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase">Fórum</div>
                        </div>
                        <div className="p-8 text-left">
                          <p className="text-[10px] text-gray-400 font-black uppercase mb-1 border-b border-red-600 w-fit">{formatDateSafe(t.created_at)}</p>
                          <h3 className="text-xl font-bold text-gray-800 line-clamp-1 mb-4 uppercase">{t.title}</h3>
                          <div className="flex justify-between items-center border-t pt-4">
                            <span className="text-xs text-gray-500 italic">💬 Participar</span>
                            <span className="text-indigo-600 font-bold text-xs uppercase">Acessar →</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }) : <div className="text-gray-400 italic">Nenhuma discussão recente.</div>}
              </div>
            </div>

            {/* PUBLICAÇÕES */}
            <div className="flex flex-col items-center w-full">
              <div className="mb-10 w-full max-w-sm bg-rose-600 p-4 rounded-2xl transform skew-x-12 text-white font-black text-center shadow-lg uppercase tracking-widest">Publicações</div>
              <div className="relative flex items-center justify-center perspective-2000 h-[480px] w-full">
                {articles.length === 0 ? <div className="text-gray-400 font-bold animate-pulse">Buscando novidades...</div> : articles.map((art, index) => {
                  const pos = (index - articleIndex + articles.length) % articles.length;
                  const styles = pos === 0 ? { transform: "translateZ(300px)", opacity: 1, zIndex: 40 } :
                                   pos === 1 ? { transform: "translateX(50%) scale(0.8)", opacity: 0.3, zIndex: 10 } :
                                   pos === articles.length - 1 ? { transform: "translateX(-50%) scale(0.8)", opacity: 0.3, zIndex: 10 } :
                                   { transform: "translateZ(-500px)", opacity: 0 };
                  const thumb = getPublicationThumb(art, settings);
                  return (
                    <div key={art.id} style={styles} onClick={() => navigate(`/publicacoes/${art.id}`)} className="absolute w-full max-w-[340px] transition-all duration-1000 cursor-pointer group">
                      <div className="bg-white rounded-[2.5rem] shadow-2xl overflow-hidden border border-gray-100 h-[380px] flex flex-col">
                        <div className="relative h-44 overflow-hidden flex items-center justify-center bg-gray-50">
                          <img src={thumb.src} className={`relative z-10 transition-all duration-700 ${thumb.type === 'image' ? 'w-full h-full object-cover group-hover:scale-110' : 'w-[85%] h-[85%] object-contain'}`} alt={art.title} />
                        </div>
                        <div className="p-6 text-left flex flex-col flex-grow">
                          <h3 className="text-lg font-bold text-gray-800 line-clamp-2 mb-2 uppercase">{art.title}</h3>
                          <div className="text-xs text-gray-500 line-clamp-3 mb-4" dangerouslySetInnerHTML={{ __html: safeParseContent(art.content) }} />
                          <div className="mt-auto pt-4 border-t flex justify-between items-center text-[10px] font-bold text-gray-400">
                            <span>{formatDateSafe(art.created_at)}</span>
                            <span className="text-rose-600 uppercase">Ler Artigo →</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* VITRINE DE PRODUTOS */}
      {products.length > 0 && (
        <section className="py-12 bg-emerald-50/30">
          <div className="container mx-auto px-6">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <div className="h-1 w-12 bg-emerald-500 rounded-full"></div>
                <h2 className="text-2xl font-black uppercase tracking-tighter text-emerald-900 text-shadow-sm">Nossa Vitrine</h2>
              </div>
              <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest">Loja Oficial</span>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {products.map((prod) => (
                <div key={prod.id} className="group bg-white rounded-3xl p-3 border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-500 relative overflow-hidden flex flex-col">
                  {prod.old_price && (
                    <div className="absolute top-4 right-4 z-10 bg-emerald-500 text-white text-[10px] font-black px-2 py-1 rounded-lg shadow-lg rotate-12 group-hover:rotate-0 transition-transform">
                      PROMOÇÃO
                    </div>
                  )}
                  
                  {/* Link para detalhes na imagem */}
                  <div 
                    onClick={() => navigate(`/produto/${prod.id}`)}
                    className="aspect-square rounded-2xl overflow-hidden bg-gray-50 mb-4 flex items-center justify-center cursor-pointer"
                  >
                    <img 
                      src={prod.image_url || settings.SITE_LOGO} 
                      alt={prod.name} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>

                  <div className="px-2 flex-grow">
                    {/* Link para detalhes no título */}
                    <h3 
                      onClick={() => navigate(`/produto/${prod.id}`)}
                      className="font-bold text-gray-800 text-sm md:text-base line-clamp-2 min-h-[3rem] mb-2 cursor-pointer hover:text-emerald-600 transition-colors"
                    >
                      {prod.name}
                    </h3>
                    <div className="flex flex-col mb-4">
                      {prod.old_price && (
                        <span className="text-xs text-gray-400 line-through">
                          {Number(prod.old_price).toLocaleString('pt-br',{style: 'currency', currency: 'BRL'})}
                        </span>
                      )}
                      <span className="text-xl font-black text-emerald-600 italic">
                        {Number(prod.price).toLocaleString('pt-br',{style: 'currency', currency: 'BRL'})}
                      </span>
                    </div>
                  </div>

                  <button 
                    onClick={() => handlePurchase(prod)} 
                    className="w-full py-3 bg-gray-900 text-white rounded-xl text-center text-xs font-bold uppercase tracking-widest hover:bg-emerald-600 transition-colors duration-300 shadow-md group-hover:shadow-emerald-200 flex items-center justify-center gap-2"
                  >
                    🛒 Comprar Agora
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* GALERIA */}
      <section className="py-14">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-black mb-2 uppercase tracking-tighter text-gray-800">Galeria de Fotos</h2>
          <p className="text-gray-500 mb-10">Destaques da nossa plataforma</p>
          <GalleryPhotoSlider />
          <div className="mt-8">
            <button onClick={() => navigate("/galeria?tab=fotos")} className="px-10 py-3 bg-indigo-600 text-white font-bold rounded-2xl hover:bg-indigo-700 transition shadow-xl active:scale-95">Ver galeria completa</button>
          </div>
        </div>
      </section>

      {/* DIFERENCIAIS */}
      <section className="py-16 bg-black/5">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-black text-center mb-12 uppercase tracking-tighter text-gray-800">Nosso Diferencial</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: "📚", title: "Conteúdo Premium", desc: "Materiais exclusivos e atualizados mensalmente." },
              { icon: "⚡", title: "Acesso Ilimitado", desc: "Sua plataforma disponível 24h por dia, 7 dias por semana." },
              { icon: "🏆", title: "Certificação", desc: "Reconhecimento oficial em todos os cursos concluídos." }
            ].map((feat, i) => (
              <div key={i} className="bg-white p-8 rounded-[2rem] shadow-sm border border-gray-100 text-center hover:shadow-xl transition-shadow">
                <div className="text-4xl mb-4">{feat.icon}</div>
                <h3 className="text-xl font-bold mb-2 text-gray-800">{feat.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        .perspective-2000 { perspective: 2000px; }
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .line-clamp-3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
        .text-shadow-sm { text-shadow: 0 1px 2px rgba(0,0,0,0.05); }
        .animate-fadeIn { animation: fadeIn 0.8s ease-out forwards; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}